import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { SignInFormState, signInSchema } from '../auth/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import YSaatioUnauthenticatedPageContainer from './YSaatioUnauthenticatedPageContainer';
import YSaatioLoginForm from './YSaatioLoginForm';
import { useKennoTenant } from '../../context/kennoTenantContext';
import { useMessageGetter } from 'react-message-context';
import { SignInPageLocationState, UNAUTHORIZED_ERROR_TYPE } from '../../types/exceptions';
import { formatDate, scrollToTop } from '../../utils';
import { loginUser } from '../../context/kennoTenantActions';
import Loader from '../../components/basic/Loader';
import SignInOneTimePassword from '../auth/SignInOneTimePassword';
import { requestConfirmationCode } from '../../apis_kenno';
import { RouteComponentProps } from 'react-router';
import { YSaatioRoutes } from '../../constants/routes';

const YSaatioSignInPage: React.FC<RouteComponentProps<{}, {}, SignInPageLocationState>> = ({ history, location }) => {
  const t = useMessageGetter('signin');
  const [{ user }, kennoTenantDispatch] = useKennoTenant();
  const [isBusy, setIsBusy] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [otpRequired, setOtpRequired] = useState<boolean>(false);

  const formMethods = useForm<SignInFormState>({ resolver: zodResolver(signInSchema) });

  /**
   * Redirects the user back to the page they were trying to view.
   * If the page was the signin page, redirect to home.
   */
  const navigateToSite = () => {
    // `from` is set by AuthenticatedRoute when redirecting here
    const from: string | undefined = (location.state as any)?.from;
    if (!from || from === YSaatioRoutes.SIGN_IN) {
      history.push(YSaatioRoutes.HOME);
    } else {
      history.push(from);
    }
  };

  if (user.isLoggedIn) {
    navigateToSite();
  }

  const handleLoginErrorTypes = useCallback(
    async (statusCode?: number, errorType?: UNAUTHORIZED_ERROR_TYPE, data?: any) => {
      if (statusCode === 401) {
        switch (errorType) {
          case UNAUTHORIZED_ERROR_TYPE.INCORRECT_CREDENTIALS:
            setErrorMessage(t('error.incorrectCredentials'));
            break;
          case UNAUTHORIZED_ERROR_TYPE.CONTRACT_NOT_STARTED:
            setErrorMessage(
              t('error.contractNotStarted', {
                startDate: formatDate(data.startDate),
              })
            );
            break;
          case UNAUTHORIZED_ERROR_TYPE.CONTRACT_EXPIRED:
            setErrorMessage(t('error.contractExpired'));
            break;
          case UNAUTHORIZED_ERROR_TYPE.CONTRACT_NOT_FOUND:
            setErrorMessage(t('error.contractNotFound'));
            break;
          case UNAUTHORIZED_ERROR_TYPE.CONFIRMATION_CODE_REQUIRED:
            // In case user did not finish registration, they must input their OTP now
            // Show the form and send them a new OTP
            const username = formMethods.getValues('username');
            try {
              await requestConfirmationCode(username);
              setErrorMessage('');
              setOtpRequired(true);
            } catch (e) {
              setErrorMessage(t('error.general'));
            }
            break;
          default:
            setErrorMessage(t('error.general'));
            break;
        }
      } else {
        setErrorMessage(t('error.general'));
      }
      setLoginFailed(true);
      setIsBusy(false);
    },
    [formMethods, t]
  );

  useEffect(() => {
    if (location.state?.loginError) {
      const { statusCode, errorType, data } = location.state.loginError;
      handleLoginErrorTypes(statusCode, errorType, data);
    }
  }, [location.state, handleLoginErrorTypes]);

  const onSubmit: SubmitHandler<SignInFormState> = async (data) => {
    scrollToTop();
    setLoginFailed(false);
    setIsBusy(true);
    try {
      await loginUser(kennoTenantDispatch, data);
      navigateToSite();
    } catch (error: any) {
      handleLoginErrorTypes(error);
    }
  };

  const onCancelOtpStep = () => {
    setOtpRequired(false);
    setLoginFailed(false);
    formMethods.reset();
  };

  if (isBusy || !user.isLoaded) {
    return (
      <YSaatioUnauthenticatedPageContainer>
        <Loader />
      </YSaatioUnauthenticatedPageContainer>
    );
  }

  if (otpRequired) {
    return (
      <YSaatioUnauthenticatedPageContainer>
        <SignInOneTimePassword
          username={formMethods.getValues('username')}
          onSuccess={formMethods.handleSubmit(onSubmit)}
          onCancel={onCancelOtpStep}
        />
      </YSaatioUnauthenticatedPageContainer>
    );
  }

  return (
    <YSaatioUnauthenticatedPageContainer>
      <FormProvider {...formMethods}>
        <YSaatioLoginForm
          onSubmit={formMethods.handleSubmit(onSubmit)}
          loginFailed={loginFailed}
          errorMessage={errorMessage}
        />
      </FormProvider>
    </YSaatioUnauthenticatedPageContainer>
  );
};

export default YSaatioSignInPage;
